import React, { useEffect, useState } from "react";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { useNavigate } from "react-router-dom";
import Loader from "../Loader";
import "../../styles/AllUsers.css";

const AllUsers = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  // Helper function to format numbers with commas and 2 decimal places
  const formatBalance = (balance) => {
    return balance.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,"); // Adds commas and fixes to 2 decimals
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // Reference the 'users' collection in Firestore
        const usersCollectionRef = collection(db, "users");
        const snapshot = await getDocs(usersCollectionRef);

        // Map the documents into an array of user data
        const usersList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Filter out users without fullName
        const validUsers = usersList.filter(
          (user) => user.fullName && user.fullName.trim() !== "",
        );

        // Fetch transactions for each valid user and calculate balance
        const updatedUsers = await Promise.all(
          validUsers.map(async (user) => {
            const userDoc = await getDoc(doc(db, "users", user.id));
            const userData = userDoc.data();
            const transactions = userData.transactions || [];

            // Calculate balance
            const totalCredits = transactions
              .filter((transaction) => transaction.type === "credit")
              .reduce((acc, transaction) => acc + transaction.amount, 0);

            const totalDebits = transactions
              .filter((transaction) => transaction.type === "debit")
              .reduce((acc, transaction) => acc + transaction.amount, 0);

            const balance = totalCredits - totalDebits;

            return {
              ...user,
              balance: balance || 0,
            };
          }),
        );

        // Sort users by balance in descending order (highest balance at the top)
        const sortedUsers = updatedUsers.sort((a, b) => b.balance - a.balance);

        setUsers(sortedUsers);
        setFilteredUsers(sortedUsers);
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        // Stop the loader
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    // Filter users based on search query
    if (searchQuery) {
      const filtered = users.filter(
        (user) =>
          user.fullName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [searchQuery, users]);

  const handleRowClick = (userId) => {
    // Navigate to the user details page
    navigate(`/userdetail/${userId}`);
  };

  const handleAddTransactionClick = () => {
    // Navigate to the add transaction page
    navigate("/userdetail/add-transaction");
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="users-container pager">
      <h1 className="users-title">Registered Users</h1>

      <div className="search-container">
        <input
          type="text"
          className="search-input"
          placeholder="Search by name or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="add-transaction-container">
        <button
          className="add-transaction-btn"
          onClick={handleAddTransactionClick}
        >
          Add Transaction
        </button>
      </div>

      {filteredUsers.length === 0 ? (
        <p className="no-users">No users found.</p>
      ) : (
        <div className="users-list">
          {filteredUsers.map((user) => (
            <div 
              key={user.id} 
              className="user-row" 
              onClick={() => handleRowClick(user.id)}
            >
              <div className="user-details">
                <span className="user-name">{user.fullName}</span>
                <span className="user-email">{user.email || "N/A"}</span>
              </div>
              <div className="user-balance-col">
                <span className="user-balance-label">Balance: </span>
                <span className="user-balance">${formatBalance(user.balance || 0)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AllUsers;
